import {RouteMetaProcessor, RouterMetaProcessorResult} from '@/global/types/router';
import {RouteLocationNormalizedGeneric} from 'vue-router';
import {AUTOLOGIN_ENABLED} from '@/constants';
import store from '@/store';
import {startAutoLogin} from '@/helpers/auth';
import permissionsHelper from '@helpers_permissions';
import {Permission} from '@/global/types/permission';

export const waitUserDataLoaded = async (): Promise<void> => {
    return new Promise((resolve): void => {
        // Check every 10 milliseconds if user profile is loaded
        const interval = setInterval(
            (): void => {
                if (null !== store.getters['user/profile/equipment_submitted'] && store.getters['user/loaded']) {
                    clearInterval(interval);
                    resolve()
                }
            },
            10
        )
    });
}

export const processRequireGuest: RouteMetaProcessor = async (to: RouteLocationNormalizedGeneric): Promise<RouterMetaProcessorResult> => {
    return Promise.resolve<RouterMetaProcessorResult>(
        to.meta?.requiresGuest && store.getters['user/isAuthenticated']
            ? {name: 'auth.logout'}
            : null
    )
}

export const processRequireAuth: RouteMetaProcessor = async (to: RouteLocationNormalizedGeneric): Promise<RouterMetaProcessorResult> => {
    return new Promise<RouterMetaProcessorResult>((resolve): any => {
        if (to.meta.requiresAuth && !store.getters['user/isAuthenticated']) {
            if (AUTOLOGIN_ENABLED) {
                // Start auto login process. It submits form to API. So, the promise does not need to be resolved
                startAutoLogin({targeturi: to.fullPath as string});
                return;
            }

            return resolve({
                name: 'auth.login',
                query: {
                    ...to.query,
                    targeturi: to.path
                }
            });
        }

        return resolve(null);
    });
}

export const processRequireUserAcknowledgement: RouteMetaProcessor = async (to: RouteLocationNormalizedGeneric): Promise<RouterMetaProcessorResult> => {
    return new Promise<RouterMetaProcessorResult>(async (resolve) => {

        if (!to.meta?.requiresAuth) {
            // For pages that do not require authentication resolve by default
            return resolve(null)
        }

        await waitUserDataLoaded();

        // Check for user needs to have approved acknowledgement
        if (
            'acknowledgement' !== to.name
            && store.getters['user/requireAcknowledgement']
            && !store.getters['user/acknowledgement']
        ) {
            // It's not acknowledgement page + user requires acknowledgement consent, but does not have it
            return resolve({name: 'acknowledgement', query: {targeturi: to.fullPath}});
        } else if (
            'acknowledgement' === to.name
            && (store.getters['user/acknowledgement'] || !store.getters['user/requireAcknowledgement'])
        ) {

            // Forbid user to be on acknowledgement page is consent already provided
            return resolve({name: 'home'});
        }

        resolve(null)
    });
}

export const processRequireUserPermissions: RouteMetaProcessor = async (to: RouteLocationNormalizedGeneric): Promise<RouterMetaProcessorResult> => {
    return new Promise<RouterMetaProcessorResult>(async (resolve) => {

        if (!to.meta?.requiresAuth) {
            // For pages that do not require authentication resolve by default
            return resolve(null)
        }

        await waitUserDataLoaded();

        if (to.meta?.permissions && 0 < to.meta?.permissions.length) {
            const permissions: Permission[] = to.meta.permissions;
            for (let i = 0; i < permissions.length; i++) {
                if (store.getters['user/permissions/hasPermissionAccess'](permissions[i])) {

                    // User has needed permission. So, no further checks required
                    resolve(null);
                    return;
                }
            }

            // Resolve by first permission (it could be any of them because nothing passed validation)
            resolve(
                permissionsHelper.getRouteByPermissionFail(
                    permissions[0],
                    store.getters["user/permissions/hasRestriction"](permissions[0])
                        ? store.getters["user/permissions/getRestriction"](permissions[0])
                        : null
                )
            )
        }

        return resolve(null)
    })
}

export const processRequireRedirect: RouteMetaProcessor = async (to: RouteLocationNormalizedGeneric): Promise<RouterMetaProcessorResult> => {

    return new Promise<RouterMetaProcessorResult>(async (resolve) => {

        if ('function' !== typeof to.meta?.requiresRedirect) {
            // For pages that do not require authentication resolve by default
            return resolve(null)
        }

        return resolve(to.meta.requiresRedirect())
    })
}

export const processRequireUserData: RouteMetaProcessor = async (to: RouteLocationNormalizedGeneric): Promise<RouterMetaProcessorResult> => {

    return new Promise<RouterMetaProcessorResult>(async (resolve) => {

        if (!to.meta?.requiresAuth) {
            // For pages that do not require authentication resolve by default
            return resolve(null)
        }

        await waitUserDataLoaded();

        if (
            store.getters['user/profile/forceQuizEquipment']
            && !['quiz', 'auth.logout', 'surveys.view'].includes(to.name as string)
        ) {
            // Force user to pass quiz
            return resolve({name: 'quiz', params: {init: 'true'}});
        }

        return resolve(null)
    })
}
